import { useQueryClient } from '@tanstack/react-query';
import { Button, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar.tsx';
import type { MydayType } from '../../fetchHooks/useMyday.ts';
import type { UserType } from '../../fetchHooks/useUserProfile.ts';
import { CreateMyday } from './CreateMyday.js';
import { DiaryTable } from './DiaryTable.js';
import { EditMyday } from './EditMyday.js';
import { NotesTable } from './NotesTable.js';
import './Myday.scss';
import { MyDayVariant, type MyDayVariantType } from './MyDayPage.tsx';

interface FurutoppenPageProps {
  userProfile: UserType;
}
export const FurutoppenPage = ({ userProfile }: FurutoppenPageProps) => {
  const { t } = useTranslation();
  const [showCreateMydayModal, setShowCreateMydayModal] = useState(false);
  const [showEditMydayModal, setShowEditMydayModal] = useState<MydayType | false>(false);
  const queryClient = useQueryClient();

  const getTabStorageKey = () => `mydayActiveTab_${userProfile?.id ?? 'anonymous'}`;

  const [activeKey, setActiveKey] = useState<string>(() => {
    const key = getTabStorageKey();
    return sessionStorage.getItem(key) || MyDayVariant.DIARY;
  });

  const handleCreateMyday = () => {
    setShowCreateMydayModal(false);
    // Let React Query handle the refetch through invalidation
    queryClient.invalidateQueries({ queryKey: ['useMydays'] });
    queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypesWithMeasurements'] });
  };

  const onOk = () => {
    setShowEditMydayModal(false);
    // Let React Query handle the refetch through invalidation
    queryClient.invalidateQueries({ queryKey: ['useMydays'] });
    queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypesWithMeasurements'] });
  };

  type TabKey = string;

  const handleTabChange = (key: TabKey): void => {
    setActiveKey(key);
    sessionStorage.setItem(getTabStorageKey(), key);
  };

  return (
    <div>
      <Navbar />
      <div className="app-container">
        <h2>{userProfile?.firstname + "'s " + t('myday.title')}</h2>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', marginBottom: '1rem' }}>
            <Button type="primary" onClick={() => setShowCreateMydayModal(true)}>
              {t('myday.createNew')}
            </Button>
            <Link className="button-link" to={`/export?tab=${activeKey}`}>
              {t('myday.exportToPDF')}
            </Link>
          </div>
          <Tabs
            activeKey={activeKey}
            onChange={handleTabChange}
            items={[
              {
                label: 'Hyttebok',
                key: MyDayVariant.DIARY,
                children: <DiaryTable setShowEditMydayModal={setShowEditMydayModal} />,
              },
              {
                label: 'Notater',
                key: MyDayVariant.NOTE,
                children: <NotesTable setShowEditMydayModal={setShowEditMydayModal} />,
              },
            ]}
          />
          {showCreateMydayModal && (
            <CreateMyday
              showModal={showCreateMydayModal}
              onClose={() => setShowCreateMydayModal(false)}
              onOk={handleCreateMyday}
              userProfile={userProfile}
              type={activeKey as MyDayVariantType}
            />
          )}
          {showEditMydayModal && (
            <EditMyday
              showModal={!!showEditMydayModal}
              onClose={() => setShowEditMydayModal(false)}
              onOk={onOk}
              userProfile={userProfile}
              myday={showEditMydayModal as MydayType}
            />
          )}
        </div>
      </div>
    </div>
  );
};
